import { useCallback, useEffect, useRef, useState } from 'react';
import Logger from 'utils/logger';
import { syncAudioToVideo } from '../utils/syncAudioToVideo';
import { isAbortError } from '../utils/isAbortError';

type Params = {
  videoSrc: string | null;
  audioSrc: string | null;
  audioVolume: number;
  videoVolume: number;
};

export type UseAudioVideoMixPreviewResult = {
  videoRef: React.RefObject<HTMLVideoElement>;
  audioRef: React.RefObject<HTMLAudioElement>;
  isPlaying: boolean;
  isBuffering: boolean;
  toggle: () => void;
  stop: () => void;
};

export function useAudioVideoMixPreview({
  videoSrc,
  audioSrc,
  audioVolume,
  videoVolume,
}: Params): UseAudioVideoMixPreviewResult {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  const stop = useCallback(() => {
    videoRef.current?.pause();
    audioRef.current?.pause();
    setIsPlaying(false);
    setIsBuffering(false);
  }, []);

  const toggle = useCallback(() => {
    if (isPlaying) {
      stop();

      return;
    }

    const video = videoRef.current;
    const audio = audioRef.current;

    /**
     * Both play() calls must stay inside the user gesture — unmuted audio can
     * never be started outside one.
     */
    video?.play().catch(Logger.error);

    if (audio) {
      audio.currentTime = video?.currentTime ?? 0;
      audio.play().catch((error) => {
        if (isAbortError(error)) return;

        Logger.error(error);
        stop();
      });
    }

    setIsPlaying(true);
  }, [isPlaying, stop]);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    return syncAudioToVideo(videoRef.current, audio, {
      audioVolume,
      videoVolume,
      onPlayingChange: setIsPlaying,
      onBufferingChange: setIsBuffering,
    });
  }, [videoSrc, audioSrc, audioVolume, videoVolume]);

  useEffect(() => stop, [stop, videoSrc, audioSrc]);

  return { videoRef, audioRef, isPlaying, isBuffering, toggle, stop };
}
